import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import NewsCard from "../components/NewsCard";
import Loading from "../components/Loading";
import { useAuth } from "../context/AuthContext";
import AdminContentForm, { AdminNewButton, AdminItemWrap, useAdminSection } from "../components/AdminContentForm";
import TopCreations from "../components/TopCreations";
import { Radio, Search } from "lucide-react";

export default function NewsPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [site, setSite] = useState("");
  const [sites, setSites] = useState([]);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const LIMIT = 12;

  useEffect(() => { api.get("/news/sites").then(r => setSites(r.data.sites || [])).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: LIMIT, offset };
    if (q) params.search = q;
    if (site) params.news_site = site;
    api.get("/news", { params })
      .then(r => { setArticles(r.data.results || []); setTotal(r.data.count || 0); })
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [q, site, offset]);
  useEffect(() => { load(); }, [load]);

  const admin = useAdminSection("news", load);

  return (
    <div data-testid="page-news">
      {/* Header */}
      <div className="mb-8 border-b border-[color:var(--sat-border)] pb-6">
        <div className="flex items-center gap-3 mb-2">
          <Radio size={16} className="sat-cyan" />
          <span className="sat-label sat-cyan">CHANNEL // 001</span>
          <div className="ml-auto flex items-center gap-2 border border-[color:var(--sat-live)] px-2 py-1">
            <span className="sat-live-dot" />
            <span className="font-mono text-[10px] tracking-widest text-[color:var(--sat-live)]">LIVE FEED</span>
          </div>
        </div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tighter">
          ASTRONATLAS<span className="sat-cyan sat-glow">.</span>
        </h1>
        <div className="mt-1 flex items-center justify-between gap-3 flex-wrap">
          <div className="sat-label sat-cyan">TRANSMISSIONS</div>
          {isAdmin && <AdminNewButton section="news" label="Article" onClick={admin.openNew} />}
        </div>
        <p className="mt-4 text-sm text-[color:var(--sat-text-dim)] max-w-2xl">
          Aggregated dispatches from mission-control desks, orbital press pools, and independent aerospace correspondents.
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--sat-text-dim)]" />
          <input
            type="search" placeholder="INTERCEPT TRANSMISSIONS…"
            value={q} onChange={(e) => { setOffset(0); setQ(e.target.value); }}
            className="!pl-10" data-testid="news-search"/>
        </div>
        <div className="flex gap-2 overflow-x-auto">
          <button data-testid="news-filter-all" onClick={() => { setSite(""); setOffset(0); }}
            className={`sat-btn !py-2 !px-4 flex-shrink-0 ${!site ? "sat-btn-solid" : ""}`}>ALL</button>
          {sites.slice(0, 6).map(s => (
            <button key={s} data-testid={`news-filter-${s.replace(/\s/g, '-').toLowerCase()}`}
              onClick={() => { setSite(s); setOffset(0); }}
              className={`sat-btn !py-2 !px-4 flex-shrink-0 whitespace-nowrap ${site === s ? "sat-btn-solid" : ""}`}>
              {s.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {loading ? <Loading message="INTERCEPTING TRANSMISSIONS" /> : (
        <>
          <TopCreations />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {articles.map(a => (
              <AdminItemWrap key={a.id} item={a} isAdmin={isAdmin} onEdit={admin.openEdit} onDelete={admin.onDelete}>
                <NewsCard article={a} />
              </AdminItemWrap>
            ))}
          </div>
          {articles.length === 0 && <div className="text-center py-16 sat-label">NO TRANSMISSIONS FOUND</div>}
          {total > LIMIT && (
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-[color:var(--sat-border)]">
              <button className="sat-btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))} data-testid="news-prev">← PREV</button>
              <span className="sat-label">{offset + 1}–{Math.min(offset + LIMIT, total)} OF {total}</span>
              <button className="sat-btn" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)} data-testid="news-next">NEXT →</button>
            </div>
          )}
        </>
      )}
      {admin.formOpen && (
        <AdminContentForm
          section="news"
          initial={admin.editing}
          initialId={admin.editing?.admin_id}
          onClose={admin.closeForm}
          onSaved={admin.onSaved}
        />
      )}
    </div>
  );
}
